/*
 * Profile wizard — pure model (gating, validation-banner copy, phone engine,
 * done-screen summary). Transcribed from the App-shell canon §5.1–§5.6
 * (needByStep / okStep / summary in the DC source). No IO here — unit-tested.
 *
 * SELF SUBJECT ONLY this cycle. The managed-child variant (subject switch,
 * age-band gate, junior sizes, guardian prefills) is deferred to the expected
 * V1.1 kids canon amendment — see TODO(V1.1 kids amendment §12.2) seams.
 */
import { KEEPS_TRUNK_ZERO } from '../../auth/cognitoProfile'
import { countryByDial, countryByIso, REF_COUNTRIES, type RefCountry } from './referenceData'
import type { WizardEventFields } from './wizardConfig'

// ---------------------------------------------------------------------------
// Draft record — every field the self wizard reads/writes
// ---------------------------------------------------------------------------

export interface WizardDraft {
  first: string
  last: string
  dobD: string
  dobM: string
  dobY: string
  /** Picked gender value only (canon §11.10 — never free text)… */
  gender: string
  /** …except the flagged-policy self-describe free text (canon §5.2). */
  selfText: string
  /** Nationality demonym — set ONLY by a typeahead pick. */
  nationality: string
  natIso: string
  resCountry: string
  resIso: string
  /** City — valid only when picked from the residence country's list. */
  city: string
  addr: string
  /** Dial select value `{ISO} +{dial}`, e.g. `AE +971`. */
  dial: string
  /** National number as typed (digits/spaces/dashes; trunk 0 stripped live). */
  phone: string
  ecName: string
  ecDial: string
  ecPhone: string
  ecRel: string
  language: string
  shirt: string
}

export const EMPTY_DRAFT: WizardDraft = {
  first: '', last: '', dobD: '', dobM: '', dobY: '', gender: '', selfText: '',
  nationality: '', natIso: '', resCountry: '', resIso: '', city: '', addr: '',
  dial: '', phone: '', ecName: '', ecDial: '', ecPhone: '', ecRel: '',
  language: '', shirt: '',
}

// ---------------------------------------------------------------------------
// Fixed option sets (canon §5.2 / §5.4 / §5.5 — verbatim)
// ---------------------------------------------------------------------------

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1))

/** Self year range: 2012 down to 1940, 73 entries (canon §5.2 — "a child's
 *  birth year can be recent; an adult entrant's cannot").
 *  TODO(V1.1 kids amendment §12.2): child range is 2026 down, 22 entries. */
export const SELF_YEARS = Array.from({ length: 73 }, (_, i) => String(2012 - i))

export const RELATIONSHIPS = ['Partner', 'Parent', 'Child', 'Sibling', 'Friend']

export const LANGUAGES = ['English', 'العربية', 'Français', 'Español', 'Deutsch', 'Italiano', 'Português', '中文']

export const SELF_SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL']
// TODO(V1.1 kids amendment §12.2): junior sizes 3-4y…13-14y + age suggestion.

/** Gender pill sets + helpbox copy per policy (canon §5.2 — verbatim;
 *  organiser name is [demo] in the strings' `{organiser}` slot). */
export const GENDER_MODES = {
  binary: {
    opts: ['Female', 'Male'],
    help: (org: string) => `Set by ${org} for results and age categories.`,
  },
  flagged: {
    opts: ['Female', 'Male', 'Prefer to self-describe'],
    help: (org: string) => `Set by ${org} for results and age categories.`,
  },
  third: {
    opts: ['Women', 'Men', 'Open'],
    help: (org: string) => `${org} publishes three results tables — Women, Men and Open.`,
  },
} as const

export const SELF_DESCRIBE_PILL = 'Prefer to self-describe'

// ---------------------------------------------------------------------------
// Phone engine (canon §5.3 — dial + national number, E.164 rules)
// Reuses the shipped trunk-zero prior art: KEEPS_TRUNK_ZERO from
// src/auth/cognitoProfile.ts (['+39'] — Italy keeps its leading 0).
// Digit rules come from the prototype reference data (see referenceData.ts
// §11.8 seam — production: libphonenumber).
// ---------------------------------------------------------------------------

export const DEFAULT_DIAL = 'AE +971'

/** `AE +971` → `971`; tolerates a bare `971` (DC). */
export const dialOf = (label: string): string =>
  (String(label || '').includes('+') ? String(label).split('+')[1] : String(label || '')).trim()

const digitsOf = (v: string): string => (v || '').replace(/\D/g, '')

const keepsTrunkZero = (dial: string): boolean => KEEPS_TRUNK_ZERO.includes(`+${dial}`)

const ruleFor = (dial: string): { min: number; max: number } =>
  countryByDial(dial) ?? { min: 6, max: 14 }

export interface PhoneState {
  dial: string
  /** Normalised national digits (trunk zero stripped unless kept). */
  digits: string
  ok: boolean
  touched: boolean
  strippedZero: boolean
  /** Hint line — verbatim canon templates. */
  hint: string
  tone: 'idle' | 'valid' | 'invalid'
}

/** Pure phone validation per the canon §5.3 rules. `raw` is the input value. */
export function phoneState(dialLabel: string, raw: string): PhoneState {
  const dial = dialOf(dialLabel || DEFAULT_DIAL)
  const r = ruleFor(dial)
  const rawDigits = digitsOf(raw)
  const dg = keepsTrunkZero(dial) ? rawDigits : rawDigits.replace(/^0+/, '')
  const strippedZero = /^0/.test(rawDigits) && !keepsTrunkZero(dial)
  const ok = dg.length >= r.min && dg.length <= r.max
  const touched = dg.length > 0 || /^0+$/.test(rawDigits)
  const need = r.min === r.max ? String(r.min) : `${r.min}–${r.max}`
  const hint = !touched
    ? `${need} digits after the country code — no leading 0`
    : strippedZero
      ? 'Leading 0 removed — numbers are stored international'
      : ok
        ? '✓ Looks right'
        : `Needs ${need} digits — you have ${dg.length}`
  return {
    dial, digits: dg, ok, touched, strippedZero, hint,
    tone: !touched ? 'idle' : ok ? 'valid' : 'invalid',
  }
}

/** Live input filter: keep digits/spaces/dashes; strip a pasted leading trunk
 *  zero to E.164 as they type (canon §5.3; KEEPS_TRUNK_ZERO exception).
 *  `stripped` drives the verbatim `Leading 0 removed — numbers are stored
 *  international` note (canon acceptance: the note shows on the paste even
 *  though the stored value no longer carries the zero). */
export function filterPhoneInput(
  dialLabel: string,
  value: string,
): { value: string; stripped: boolean } {
  const dial = dialOf(dialLabel || DEFAULT_DIAL)
  const cleaned = value.replace(/[^\d\s-]/g, '')
  const d = digitsOf(cleaned)
  const stripped = !keepsTrunkZero(dial) && /^0/.test(d)
  return { value: stripped ? d.replace(/^0+/, '') : cleaned, stripped }
}

/** Stored value is E.164: dial + national number (canon §5.3 acceptance). */
export function toE164(dialLabel: string, raw: string): string {
  const st = phoneState(dialLabel, raw)
  return st.digits ? `+${st.dial}${st.digits}` : ''
}

/** Parse a stored E.164 back into {dialLabel, national} using the reference
 *  list (longest dial-prefix match); null if unparseable. */
export function fromE164(e164: string): { dialLabel: string; national: string } | null {
  if (!e164 || !e164.startsWith('+')) return null
  const digits = e164.slice(1)
  // longest-prefix match over the reference dials
  let best: RefCountry | null = null
  for (const c of REF_COUNTRIES) {
    if (digits.startsWith(c.d) && (!best || c.d.length > best.d.length)) best = c
  }
  if (!best) return null
  return { dialLabel: `${best.iso} +${best.d}`, national: digits.slice(best.d.length) }
}

// ---------------------------------------------------------------------------
// Derived facts
// ---------------------------------------------------------------------------

export const dobFull = (d: WizardDraft): string =>
  d.dobD && d.dobM && d.dobY ? `${d.dobD} ${d.dobM} ${d.dobY}` : ''

export const genderOk = (d: WizardDraft, policy: 'binary' | 'flagged' | 'third'): boolean =>
  !!d.gender && !(policy === 'flagged' && d.gender === SELF_DESCRIBE_PILL && !d.selfText)

export const natSelected = (d: WizardDraft): RefCountry | null =>
  d.natIso ? countryByIso(d.natIso) : null

export const resSelected = (d: WizardDraft): RefCountry | null =>
  d.resIso ? countryByIso(d.resIso) : null

/** City is valid once a residence is chosen and a city is SET. `draft.city` is
 *  only ever written by a deliberate typeahead pick (a real city or the explicit
 *  "use my entry" free-text option) — free typing clears it — so a set value is
 *  always an intentional choice. This replaced the old
 *  `rc.c.includes(d.city)` allow-list, which silently blocked any city outside
 *  the tiny per-country seed (CMT-100427: e.g. Ayutthaya in Thailand). */
export const cityValid = (d: WizardDraft): boolean => {
  const rc = resSelected(d)
  return !!(rc && d.city)
}

// ---------------------------------------------------------------------------
// Step gates + validation-banner copy (canon §5 needByStep — verbatim phrases)
// ---------------------------------------------------------------------------

export interface GateContext {
  genderPolicy: 'binary' | 'flagged' | 'third'
  eventFields: WizardEventFields
}

/** Missing-item phrases for a step (1-based), self subject — canon verbatim.
 *  TODO(V1.1 kids amendment §12.2): child phrases (`the date of birth`,
 *  `a category`, `a date of birth inside the age band`, `a T-shirt size`). */
export function stepNeeds(step: number, d: WizardDraft, ctx: GateContext): string[] {
  const F = ctx.eventFields
  const ph = phoneState(d.dial, d.phone)
  const phoneOkOrEmpty = !ph.touched || ph.ok
  const ecp = phoneState(d.ecDial, d.ecPhone)
  const byStep: Array<Array<string | false>> = [
    [!d.first && 'your first name', !d.last && 'your last name'],
    [!dobFull(d) && 'your date of birth', !genderOk(d, ctx.genderPolicy) && 'your gender'],
    [
      !natSelected(d) && 'your nationality',
      !resSelected(d) && 'your country of residence',
      !cityValid(d) && 'your city',
      !!F.phone && !phoneOkOrEmpty && 'a valid phone number',
      F.phone === 'required' && !ph.touched && 'your phone number',
      F.address === 'required' && !d.addr && 'your street address',
    ],
    [!d.ecName && 'a contact name', !ecp.ok && 'a valid contact phone'],
    [!d.language && 'your language', !d.shirt && 'your T-shirt size'],
  ]
  return (byStep[step - 1] || []).filter((x): x is string => Boolean(x))
}

export const stepValid = (step: number, d: WizardDraft, ctx: GateContext): boolean =>
  stepNeeds(step, d, ctx).length === 0

/** Banner template (canon §5): one item → `Add {item} to continue`; several →
 *  `Still needed: {a}, {b} and {c}`. */
export function blockText(needs: string[]): string {
  if (needs.length === 0) return ''
  if (needs.length === 1) return `Add ${needs[0]} to continue`
  return `Still needed: ${needs.slice(0, -1).join(', ')} and ${needs[needs.length - 1]}`
}

// ---------------------------------------------------------------------------
// Done screen (canon §5.6)
// ---------------------------------------------------------------------------

/** Profile done-ness is derived from the data itself — never a stored flag
 *  (canon §2.4): first && last && DOB && gender && nationality && residence
 *  && city. */
export const allValid = (d: WizardDraft, policy: GateContext['genderPolicy']): boolean =>
  !!(d.first && d.last && dobFull(d) && genderOk(d, policy) && natSelected(d) && resSelected(d) && cityValid(d))

export function missingList(d: WizardDraft, policy: GateContext['genderPolicy']): string {
  const keys = ['name', 'date of birth', 'gender', 'nationality', 'country of residence', 'city']
  const miss = [
    !(d.first && d.last), !dobFull(d), !genderOk(d, policy),
    !natSelected(d), !resSelected(d), !cityValid(d),
  ]
  return keys.filter((_, i) => miss[i]).join(', ')
}

/** First incomplete step for `Add these now` / resume (DC `resumeAt` / `pAt`). */
export function firstIncompleteStep(d: WizardDraft, policy: GateContext['genderPolicy']): number {
  if (!d.first || !d.last) return 1
  if (!dobFull(d) || !genderOk(d, policy)) return 2
  return 3
}

export interface SummaryRow {
  k: string
  v: string
}

/** Summary card rows in canon order (§5.6); missing values render `—`. */
export function summaryRows(d: WizardDraft, ctx: GateContext): SummaryRow[] {
  const F = ctx.eventFields
  const rows: Array<SummaryRow | null> = [
    { k: 'Name', v: `${d.first} ${d.last}`.trim() || '—' },
    { k: 'Date of birth', v: dobFull(d) || '—' },
    { k: 'Gender', v: d.gender || '—' },
    d.selfText ? { k: 'Gender identity', v: d.selfText } : null,
    { k: 'Nationality', v: d.nationality || '—' },
    { k: 'Residence', v: d.city && d.resCountry ? `${d.city}, ${d.resCountry}` : '—' },
    F.address && d.addr ? { k: 'Address', v: d.addr } : null,
    F.phone
      ? { k: 'Phone', v: d.phone ? `+${dialOf(d.dial || DEFAULT_DIAL)} ${d.phone}` : 'Not given' }
      : null,
    { k: 'Emergency contact', v: d.ecName ? `${d.ecName}${d.ecRel ? ' · ' + d.ecRel : ''}` : '—' },
    { k: 'Emergency phone', v: d.ecPhone ? `+${dialOf(d.ecDial || DEFAULT_DIAL)} ${d.ecPhone}` : '—' },
    { k: 'Language', v: d.language || '—' },
    { k: 'T-shirt size', v: d.shirt || '—' },
  ]
  return rows.filter((r): r is SummaryRow => r !== null)
}
